import { PriceDisplayMode } from '~/utilities/graphql/codegen';
import { usePriceDisplayConfiguration } from '~/utilities/hooks/usePriceDisplayConfiguration';

export type TileConfigPrice = {
  type: 'rrp' | 'itp' | 'l30d';
  value: number;
  strikethrough: boolean;
};

type Params = {
  priceDisplayMode?: PriceDisplayMode | null;
  priceValue?: number | null;
  fromPriceValue?: number | null;
  intermediaryPriceValue?: number | null;
  lowestPrice?: number | null;
  hideIntermediaryPrice?: boolean;
};

/**
 * Resolves the prices a product tile shows before the base price in `LowestPriceDisplay.CONFIG`,
 * in display order.
 *
 * Only two display modes use the site configuration, every other mode shows the base price only:
 * - `DEFAULT_WITH_RRP`: RRP (`rrpDisplay`, `rrpStrikethrough`), then ITP (`itpDisplay`)
 * - `L30D_2`: L30D price (`l30dDisplay`, `l30dStrikethrough`), then ITP (`itpDisplay`)
 *
 */
export const useTileConfigPrices = ({
  priceDisplayMode,
  priceValue,
  fromPriceValue,
  intermediaryPriceValue,
  lowestPrice,
  hideIntermediaryPrice,
}: Params): TileConfigPrice[] => {
  const priceDisplayConfig = usePriceDisplayConfiguration(priceDisplayMode);

  if (!priceDisplayConfig) {
    return [];
  }

  const { displayMode, rrpDisplay, rrpStrikethrough, l30dDisplay, l30dStrikethrough, itpDisplay } =
    priceDisplayConfig;
  const basePrice = priceValue || 0;
  const rrp = fromPriceValue || 0;
  const itp = intermediaryPriceValue || 0;
  const l30d = lowestPrice || fromPriceValue || 0;

  const itpPrices: TileConfigPrice[] =
    itpDisplay && !hideIntermediaryPrice && itp > basePrice && itp < rrp
      ? [{ type: 'itp', value: itp, strikethrough: true }]
      : [];

  if (displayMode === PriceDisplayMode.DefaultWithRrp) {
    const rrpPrices: TileConfigPrice[] = rrpDisplay
      ? [{ type: 'rrp', value: rrp, strikethrough: rrpStrikethrough }]
      : [];

    return [...rrpPrices, ...itpPrices];
  }

  if (displayMode === PriceDisplayMode.L30D_2) {
    const l30dPrices: TileConfigPrice[] = l30dDisplay
      ? [{ type: 'l30d', value: l30d, strikethrough: l30dStrikethrough }]
      : [];

    return [...l30dPrices, ...itpPrices];
  }

  return [];
};
